import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getVenues } from "@/api/venues";
import { getSkills } from "@/api/skills";
import { postJob } from "@/api/jobs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import MultiDateSelector from "@/components/MultiDateSelector";

export default function JobCreateForm() {
    const navigate = useNavigate();
    const [venues, setVenues] = useState([]);
    const [skills, setSkills] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState({
        venueId: "",
        skillId: "",
        startDate: "",
        endDate: "",
        startTime: { hour: "", minute: "" },
        endTime: { hour: "", minute: "" },
        shiftDetails: "",
        selectedDates: [],
        jobDays: []
    });

    useEffect(() => {
        getVenues().then((data) => setVenues(data || []));
        getSkills().then((data) => setSkills(data || []));
    }, []);

    const handleChange = (e, path) => {
        const { name, value } = e.target;
        if (path) {
            setForm((prev) => ({
                ...prev,
                [path]: {
                    ...prev[path],
                    [name]: value
                }
            }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleDatesChange = (selectedDates) => {
        let startDate = "";
        let endDate = "";
        let jobDays = [];

        if (selectedDates.length > 0) {
            const sortedDates = [...selectedDates].sort();
            startDate = sortedDates[0];
            endDate = sortedDates[sortedDates.length - 1];
            jobDays = selectedDates.map(date => ({ date }));
        }

        setForm(prev => ({
            ...prev,
            selectedDates,
            startDate,
            endDate,
            jobDays
        }));
    };


    const buildTime = ({ hour, minute }) => {
        if (!hour || !minute) return "";
        return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    };

    const isFormValid = () => {
        const requiredFields = [
            form.venueId,
            form.skillId,
            form.startDate,
            form.endDate,
            form.startTime.hour,
            form.startTime.minute,
            form.endTime.hour,
            form.endTime.minute
        ];
        if (requiredFields.some((val) => !val)) return false;
        
        return form.selectedDates.length > 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid()) {
            toast.error("Please fill all required fields correctly.");
            return;
        }
        setIsSubmitting(true);
        const payload = {
            venueId: form.venueId,
            skillId: form.skillId,
            startDate: form.startDate,
            endDate: form.endDate,
            startTime: buildTime(form.startTime),
            endTime: buildTime(form.endTime),
            shiftDetails: form.shiftDetails,
            jobDaysAttributes: form.jobDays
        };
        console.log(payload)
        const response = await postJob(payload);
        setIsSubmitting(false);
        if (response?.id) {
            toast.success("Job created successfully");
            navigate(`/admin/jobs/${response.id}`);
        } else {
            toast.error("Failed to create job");
        }
    };

    const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
    const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

    const renderTimeSelectors = (timeKey) => (
        <div className="flex gap-2 mt-1">
            <select name="hour" value={form[timeKey].hour} onChange={(e) => handleChange(e, timeKey)} className="border rounded p-2">
                <option value="">HH</option>
                {hours.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <span>:</span>
            <select name="minute" value={form[timeKey].minute} onChange={(e) => handleChange(e, timeKey)} className="border rounded p-2">
                <option value="">MM</option>
                {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
        </div>
    );

    return (
        <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto p-6 bg-white rounded-xl shadow">
            <h2 className="text-xl font-semibold text-[#5D1F42]">Create Job</h2>

            <div>
                <Label htmlFor="venueId">Venue</Label>
                <select name="venueId" value={form.venueId} onChange={handleChange} className="w-full mt-1 border rounded p-2">
                    <option value="">Select Venue</option>
                    {venues.map((venue) => (
                        <option key={venue.id} value={venue.id}>{venue.name}</option>
                    ))}
                </select>
            </div>

            <div>
                <Label htmlFor="skillId">Position</Label>
                <select name="skillId" value={form.skillId} onChange={handleChange} className="w-full mt-1 border rounded p-2">
                    <option value="">Select Position</option>
                    {skills.map((skill) => (
                        <option key={skill.id} value={skill.id}>{skill.name}</option>
                    ))}
                </select>
            </div>

            <MultiDateSelector
                selectedDates={form.selectedDates}
                onDatesChange={handleDatesChange}
                className="mb-6"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label>Start Time</Label>
                    {renderTimeSelectors("startTime")}
                </div>
                <div>
                    <Label>End Time</Label>
                    {renderTimeSelectors("endTime")}
                </div>
            </div>

            <div>
                <Label htmlFor="shiftDetails">Shift Details</Label>
                <Textarea name="shiftDetails" value={form.shiftDetails} onChange={handleChange} rows={4} className="mt-1" />
            </div>

            <Button type="submit" className="mt-2" disabled={isSubmitting}>Create Job</Button>
        </form>
    );
}
